import { Identity } from '../components/header/models/identity';

import { getIDTokenData } from './tokenUtils';

export interface LicenseEntitlements {
  tier: string | null;
  telehealthEnabled: boolean;
  seatsEntitled: number | null;
  aiEntitled: number | null;
  graceExpires: string | null;
  featureFlags: Record<string, boolean>;
}

const parseIntegerClaim = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);

  return Number.isNaN(parsed) ? null : parsed;
};

const parseBooleanClaim = (value?: string): boolean => {
  if (!value) {
    return false;
  }

  return value.toLowerCase() === 'true';
};

const parseFeatureFlags = (value?: string): Record<string, boolean> => {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;

    return Object.fromEntries(
      Object.entries(parsed).map(([key, featureValue]) => [key, Boolean(featureValue)])
    );
  } catch (error) {
    console.warn('Failed to parse feature flags from claims', error);

    return {};
  }
};

export const parseLicenseClaims = (identity: Identity): LicenseEntitlements => {
  return {
    tier: identity['custom:tier'] ?? null,
    telehealthEnabled: parseBooleanClaim(identity['custom:telehealth_enabled']),
    seatsEntitled: parseIntegerClaim(identity['custom:seats_entitled']),
    aiEntitled: parseIntegerClaim(identity['custom:ai_entitled']),
    graceExpires: identity['custom:grace_expires'] ?? null,
    featureFlags: parseFeatureFlags(identity['custom:features']),
  };
};

export const getLicenseEntitlements = (): LicenseEntitlements => {
  const identity = getIDTokenData();

  return parseLicenseClaims(identity);
};

/**
 * HA-332: threshold above which seat counts are treated as "unlimited"
 * for display purposes. Backend tiers without a cap emit `int.MaxValue`
 * (2,147,483,647) for `custom:seats_entitled`; rendering that verbatim
 * leaks a sentinel to clinical users. Anything &gt;= 999_999 collapses to
 * "Unlimited"; below that it renders as a numeric count.
 *
 * Mirrors `usePracticeUsageStats.UNLIMITED_THRESHOLD` (HA-122). Keep the
 * two values in sync if either changes — consolidation is a separate
 * tech-debt follow-up.
 */
export const UNLIMITED_THRESHOLD = 999_999;

/**
 * HA-332: render a seat count for the Add Client / Invite Teammate
 * dialogs and the Account Management summary card. Returns
 * "Unlimited seats" for unlimited tiers (`n &gt;= UNLIMITED_THRESHOLD`),
 * pluralized "{n} seat" / "{n} seats" otherwise. Negative inputs are
 * clamped to 0 (defensive — shouldn't happen but a negative remaining-
 * seat count would itself be a sentinel smell).
 */
export const formatSeatCount = (n: number): string => {
  if (n >= UNLIMITED_THRESHOLD) {
    return 'Unlimited seats';
  }
  const safe = Math.max(0, n);

  return `${safe} seat${safe === 1 ? '' : 's'}`;
};

/**
 * HA-332b: render the "Current Users" capacity chip on Account Management.
 * For capped tiers this is "{remaining} of {entitled} seats left"; for
 * unlimited tiers (`entitled >= UNLIMITED_THRESHOLD`) it collapses to
 * "Unlimited seats" so the `int.MaxValue` sentinel (2,147,483,647) never
 * reaches a clinical user. The original `formatSeatCount` fix (HA-332)
 * covered the invite panels but missed this distinct "X of Y" template.
 * Negative remaining is clamped to 0 (defensive — same posture as
 * `formatSeatCount`).
 */
export const formatSeatsRemaining = (remaining: number, entitled: number): string => {
  // entitled <= 0 is the API's other "unlimited" value (see isUnlimitedSeatEntitlement).
  if (entitled <= 0 || entitled >= UNLIMITED_THRESHOLD) {
    return 'Unlimited seats';
  }

  return `${Math.max(0, remaining)} of ${entitled} seats left`;
};

/**
 * Whether a seat entitlement means "no cap". Mirrors the API's own rule in
 * LicenseService.EvaluateSeatCapacityAsync (`SeatsEntitled <= 0 ? int.MaxValue`):
 * trial and other uncapped tiers send 0, older uncapped tiers send int.MaxValue.
 * Treating 0 as "zero seats" disabled Add Client for every trial practice even
 * though the API accepted the invite.
 */
export const isUnlimitedSeatEntitlement = (entitled: number | null): boolean =>
  entitled !== null && (entitled <= 0 || entitled >= UNLIMITED_THRESHOLD);

/**
 * Seats left under a capped entitlement, or null when the entitlement is unknown
 * (not loaded) or unlimited — in both cases nothing should be blocked on seats.
 * May be negative when a practice is over its cap.
 */
export const getSeatsRemaining = (entitled: number | null, used: number): number | null =>
  entitled === null || isUnlimitedSeatEntitlement(entitled) ? null : entitled - used;

/**
 * "Plan capacity" wording for an entitlement: "Unlimited seats" for either
 * unlimited sentinel, otherwise the numeric count.
 */
export const formatSeatEntitlement = (entitled: number): string =>
  isUnlimitedSeatEntitlement(entitled) ? 'Unlimited seats' : formatSeatCount(entitled);
